/* Temporary AMGO freight-quote gate. */
(() => {
  const AMGO_VENDOR = "AMGO® Hydraulics";
  const AMGO_LTL_LIVE_LINE_PROPERTY = "_alamo_freight_mode";
  // Explicit stale-cart overrides. Keep empty unless a validated live variant is
  // deliberately returned to quote-only.
  const AMGO_FORCED_QUOTE_VARIANT_IDS = new Set([]);
  const STORAGE_KEY = "alamo-amgo-freight-quote-cart-v1";
  let amgoItemsInCart = [];
  let cartCheckInFlight = false;

  const cartUrl = () => window.routes?.cart_url || "/cart";
  const cartUpdateUrl = () => {
    const url = window.routes?.cart_update_url || "/cart/update";
    return url.endsWith(".js") ? url : `${url}.js`;
  };
  const gates = () => Array.from(document.querySelectorAll("[data-amgo-freight-gate]"));
  const isLtlLiveAmgoCartLine = (item) => (
    item?.vendor === AMGO_VENDOR
    && !AMGO_FORCED_QUOTE_VARIANT_IDS.has(String(item?.id || ""))
    && item?.properties?.[AMGO_LTL_LIVE_LINE_PROPERTY] === "ltl_live"
  );

  function productData(gate) {
    const node = gate.querySelector("[data-amgo-freight-product-json]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn("AMGO freight gate could not read its product data.", error);
      return null;
    }
  }

  function selectedVariant(gate, suppliedVariant) {
    if (suppliedVariant) return suppliedVariant;
    const id = gate.querySelector('.amgo-freight-variant-shadow input[name="id"]')?.value;
    return productData(gate)?.variants?.find((variant) => String(variant.id) === String(id)) || null;
  }

  function setValue(gate, selector, value) {
    const field = gate.querySelector(selector);
    if (field) field.value = value ?? "";
  }

  function syncQuote(gate, suppliedVariant) {
    const product = productData(gate);
    const variant = selectedVariant(gate, suppliedVariant);
    const quantityField = gate.querySelector("[data-amgo-freight-quantity]");
    const quantity = Math.max(1, Number.parseInt(quantityField?.value || "1", 10) || 1);
    if (quantityField) quantityField.value = quantity;
    setValue(gate, "[data-amgo-freight-product]", product?.title || "");
    setValue(gate, "[data-amgo-freight-variant]", variant?.title || "");
    setValue(gate, "[data-amgo-freight-sku]", variant?.sku || "");
    setValue(gate, "[data-amgo-freight-quantity-value]", quantity);
    setValue(gate, "[data-amgo-freight-product-url]", product?.url || window.location.href);
  }

  function readQuoteCart() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeQuoteCart(items) {
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          items: items.map((item) => ({
            title: item.product_title || item.title,
            variant: item.variant_title || "",
            sku: item.sku || "",
            quantity: item.quantity,
            url: item.url,
          })),
        }),
      );
    } catch (error) {
      console.warn("AMGO freight gate could not retain cart quote details.", error);
    }
  }

  function clearQuoteCart() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Storage is optional; the current product quote still works.
    }
  }

  function quoteSummary(saved) {
    if (!saved?.items?.length) return "";
    return saved.items
      .map((item) => {
        const variant = item.variant && item.variant !== "Default Title" ? ` — ${item.variant}` : "";
        const sku = item.sku ? ` (SKU ${item.sku})` : "";
        return `${item.quantity} × ${item.title}${variant}${sku}`;
      })
      .join("; ");
  }

  function hydrateSavedCartQuote(gate) {
    const saved = readQuoteCart();
    if (!saved?.items?.length) return;
    const summary = quoteSummary(saved);
    const panel = gate.querySelector("[data-amgo-freight-quote-panel]");
    const opener = gate.querySelector("[data-amgo-freight-open]");
    const summaryNode = gate.querySelector("[data-amgo-freight-cart-summary]");
    const field = gate.querySelector("[data-amgo-freight-cart-items]");
    if (summaryNode) {
      summaryNode.textContent = `This request also includes these AMGO cart item(s): ${summary}`;
      summaryNode.hidden = false;
    }
    if (field) field.value = summary;
    if (panel) panel.hidden = false;
    if (opener) opener.setAttribute("aria-expanded", "true");
  }

  function initializeGates() {
    gates().forEach((gate) => {
      syncQuote(gate);
      hydrateSavedCartQuote(gate);
      if (gate.querySelector(".form-status-success")) clearQuoteCart();
    });
  }

  function replaceAmgoFreeShippingMessaging() {
    const isLiveLtlProduct = Boolean(document.querySelector("[data-alamo-ltl-checkout-product]"));
    if (!gates().length && !isLiveLtlProduct) return;
    const headline = isLiveLtlProduct
      ? "Your LTL freight rate is calculated at checkout"
      : "AMGO freight is quoted by destination";
    document.querySelectorAll(".scrolling-elements-text").forEach((node) => {
      if (/Orders Ship FREE/i.test(node.textContent || "")) {
        node.textContent = headline;
      }
    });
    document.querySelectorAll(".media-icon-item-title").forEach((node) => {
      if (/^Free shipping(?: over \$100)?$/i.test((node.textContent || "").trim())) {
        node.textContent = headline;
      }
    });
  }

  function openQuote(opener) {
    const gate = opener.closest("[data-amgo-freight-gate]");
    const panel = gate?.querySelector("[data-amgo-freight-quote-panel]");
    if (!gate || !panel) return;
    syncQuote(gate);
    panel.hidden = false;
    opener.setAttribute("aria-expanded", "true");
    panel.querySelector('input[name="contact[name]"]')?.focus();
  }

  function makeCartNotice(location) {
    const notice = document.createElement("aside");
    notice.className = "amgo-freight-cart-notice";
    notice.dataset.amgoFreightCartNotice = location;
    notice.setAttribute("role", "alert");
    notice.innerHTML = `
      <p><strong>AMGO shipping needs a freight quote.</strong> Your other brands can still check out right away after the AMGO item(s) are removed from this cart.</p>
      <div class="amgo-freight-cart-notice-actions">
        <button type="button" class="button button-secondary" data-amgo-freight-quote-cart>Get freight quote for AMGO items</button>
        <button type="button" class="button" data-amgo-freight-remove-and-checkout>Remove AMGO and check out other items</button>
      </div>`;
    return notice;
  }

  function renderCartNotices() {
    document.querySelectorAll("[data-amgo-freight-cart-notice]").forEach((node) => node.remove());
    document.body.classList.toggle("amgo-freight-items-in-cart", amgoItemsInCart.length > 0);
    if (!amgoItemsInCart.length) return;
    const cartPage = document.querySelector("#main-cart-items .js-contents");
    if (cartPage) cartPage.prepend(makeCartNotice("cart-page"));
    const miniCart = document.querySelector("mini-cart .drawer-content");
    if (miniCart) miniCart.prepend(makeCartNotice("mini-cart"));
  }

  function focusCartNotice() {
    const notice = document.querySelector('[data-amgo-freight-cart-notice="cart-page"]') || document.querySelector("[data-amgo-freight-cart-notice]");
    if (!notice) return;
    notice.scrollIntoView({ behavior: "smooth", block: "center" });
    notice.querySelector("[data-amgo-freight-remove-and-checkout]")?.focus({ preventScroll: true });
  }

  async function refreshCartGuard() {
    if (cartCheckInFlight) return;
    cartCheckInFlight = true;
    try {
      const response = await fetch(`${cartUrl()}.js`, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const cart = await response.json();
      // The marker comes only from the server-rendered product form for an
      // explicit ltl_live product. Missing/direct-cart markers stay quote-only.
      amgoItemsInCart = (cart.items || []).filter(
        (item) => item.vendor === AMGO_VENDOR && !isLtlLiveAmgoCartLine(item),
      );
      renderCartNotices();
    } catch (error) {
      console.warn("AMGO freight gate could not inspect the cart.", error);
    } finally {
      cartCheckInFlight = false;
    }
  }

  function requestQuoteForCart() {
    if (!amgoItemsInCart.length) return;
    writeQuoteCart(amgoItemsInCart);
    const firstUrl = amgoItemsInCart[0].url;
    if (firstUrl) window.location.assign(firstUrl);
  }

  async function removeAmgoAndCheckout(button) {
    if (!amgoItemsInCart.length) return;
    button.disabled = true;
    button.textContent = "Removing AMGO items…";
    const updates = Object.fromEntries(amgoItemsInCart.map((item) => [item.key, 0]));
    try {
      const response = await fetch(cartUpdateUrl(), {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error("Cart update failed");
      const cart = await response.json();
      amgoItemsInCart = [];
      window.location.assign(cart.item_count > 0 ? "/checkout" : cartUrl());
    } catch (error) {
      button.disabled = false;
      button.textContent = "Remove AMGO and check out other items";
      console.error("AMGO freight gate could not remove AMGO cart items.", error);
      window.alert("We could not remove the AMGO item automatically. Please remove it from the cart, then continue checkout.");
    }
  }

  document.addEventListener("click", (event) => {
    const opener = event.target.closest?.("[data-amgo-freight-open]");
    if (opener) {
      event.preventDefault();
      openQuote(opener);
      return;
    }
    const quoteCartButton = event.target.closest?.("[data-amgo-freight-quote-cart]");
    if (quoteCartButton) {
      event.preventDefault();
      requestQuoteForCart();
      return;
    }
    const removeButton = event.target.closest?.("[data-amgo-freight-remove-and-checkout]");
    if (removeButton) {
      event.preventDefault();
      removeAmgoAndCheckout(removeButton);
    }
  });

  document.addEventListener("click", (event) => {
    if (!amgoItemsInCart.length) return;
    const trigger = event.target.closest?.('[name="checkout"], #checkout, .shopify-payment-button__button');
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusCartNotice();
  }, true);

  document.addEventListener("submit", (event) => {
    const quoteGate = event.target.closest?.("[data-amgo-freight-gate]");
    if (quoteGate) syncQuote(quoteGate);
    if (!amgoItemsInCart.length || event.submitter?.name !== "checkout") return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusCartNotice();
  }, true);

  document.addEventListener("change", (event) => {
    const gate = event.target.closest?.("[data-amgo-freight-gate]");
    if (gate) syncQuote(gate);
  });
  document.addEventListener("product:variant-change", (event) => gates().forEach((gate) => syncQuote(gate, event.detail?.variant)));
  document.addEventListener("cart:item-add", refreshCartGuard);
  document.addEventListener("cart:item-quantity-update", refreshCartGuard);
  document.addEventListener("cart:item-remove", refreshCartGuard);
  document.addEventListener("mini-cart:opened", refreshCartGuard);
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartGuard);
    subscribe(PUB_SUB_EVENTS.cartItemAdd, refreshCartGuard);
  }

  const start = () => {
    initializeGates();
    replaceAmgoFreeShippingMessaging();
    refreshCartGuard();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
